/* global globalThis */

const CSV_FORMULA_PREFIX = /^\s*[=+\-@]/;
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;

function normalizeCellValue(value) {
  return String(value ?? "").replace(/\r\n?/g, "\n");
}

/** Prevent spreadsheet applications from interpreting cell text as a formula. */
export function protectCsvFormula(value) {
  const normalized = normalizeCellValue(value);
  return CSV_FORMULA_PREFIX.test(normalized) ? `'${normalized}` : normalized;
}

export function escapeCsvCell(value) {
  const protectedValue = protectCsvFormula(value).replace(/"/g, '""');
  return `"${protectedValue}"`;
}

/** Serialize a two-dimensional value array as RFC 4180-style CSV. */
export function serializeTableMatrixToCsv(rows, { includeBom = true } = {}) {
  if (!Array.isArray(rows) || rows.length === 0) return "";

  const csv = rows
    .map((row) => {
      const values = Array.isArray(row) ? row : [row];
      return values.map(escapeCsvCell).join(",");
    })
    .join("\r\n");

  return `${includeBom ? "\uFEFF" : ""}${csv}`;
}

function extractCellText(cell) {
  const renderedText = typeof cell?.innerText === "string" ? cell.innerText : cell?.textContent;
  return normalizeCellValue(renderedText)
    .replace(/\u00a0/g, " ")
    .trim();
}

/** Extract the visual rows and cells from a rendered HTML table. */
export function tableElementToMatrix(tableElement) {
  if (!tableElement || typeof tableElement.querySelectorAll !== "function") return [];

  return Array.from(tableElement.querySelectorAll("tr"))
    .map((row) => {
      const cells = row.cells ? Array.from(row.cells) : Array.from(row.querySelectorAll("th, td"));
      return cells.map(extractCellText);
    })
    .filter((row) => row.length > 0);
}

export function sanitizeCsvFilename(filename) {
  const withoutControlCharacters = Array.from(String(filename || "generated-table"), (character) =>
    character.charCodeAt(0) <= 31 ? "-" : character
  ).join("");
  let baseName = withoutControlCharacters
    .trim()
    .replace(/\.csv$/i, "")
    .replace(INVALID_FILENAME_CHARS, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .trim();

  if (!baseName) baseName = "generated-table";
  return `${baseName.slice(0, 116)}.csv`;
}

/** Download one rendered table without a backend round trip. */
export function downloadTableAsCsv(
  tableElement,
  filename = "generated-table.csv",
  {
    documentRef = globalThis.document,
    urlApi = globalThis.URL,
    BlobCtor = globalThis.Blob,
    setTimeoutFn = globalThis.setTimeout,
  } = {}
) {
  const rows = tableElementToMatrix(tableElement);
  const csv = serializeTableMatrixToCsv(rows);
  if (!csv) throw new Error("The table has no exportable rows.");
  if (!documentRef?.body || !urlApi?.createObjectURL || !BlobCtor) {
    throw new Error("CSV downloads are not supported in this add-in environment.");
  }

  const safeFilename = sanitizeCsvFilename(filename);
  const blob = new BlobCtor([csv], { type: "text/csv;charset=utf-8" });
  const objectUrl = urlApi.createObjectURL(blob);
  const anchor = documentRef.createElement("a");

  anchor.href = objectUrl;
  anchor.download = safeFilename;
  anchor.style.display = "none";
  documentRef.body.appendChild(anchor);

  try {
    anchor.click();
  } finally {
    documentRef.body.removeChild(anchor);
    setTimeoutFn(() => urlApi.revokeObjectURL(objectUrl), 1000);
  }

  return safeFilename;
}
